'use client';

import { useEffect, useRef } from 'react';

import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $getRoot, $insertNodes, $setSelection } from 'lexical';

export type HtmlImportExportPluginProps = {
  /**
   * HTML string to set the initial content of the editor.
   * When this prop changes, the editor content will be updated.
   */
  value?: string;
  /**
   * Callback function that is called whenever the editor content changes.
   * The callback receives the current HTML representation of the editor content.
   */
  onChange?: (html: string) => void;
};

/**
 * Plugin component that handles HTML import/export for controlled editor
 */
export function HtmlImportExportPlugin({
  value,
  onChange,
}: HtmlImportExportPluginProps) {
  const [editor] = useLexicalComposerContext();
  const previousValueRef = useRef<string | undefined>(undefined);
  const previousHtmlRef = useRef<string>('');
  const isUpdatingRef = useRef(false);

  // Keep the latest onChange in a ref so the update listener below can register
  // once instead of re-subscribing on every render (parents typically pass an
  // inline function whose identity changes each render).
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Handle HTML import when value prop changes
  useEffect(() => {
    if (value === undefined || value === previousValueRef.current) {
      return;
    }

    previousValueRef.current = value;

    // Skip if we're currently updating from onChange to avoid loops
    if (isUpdatingRef.current) {
      return;
    }

    // Only let the import move DOM focus into the editor if the user was
    // already focused inside it. Otherwise the pending selection created below
    // would steal focus from wherever the user currently is.
    const rootElement = editor.getRootElement();
    const hadFocus =
      rootElement !== null && rootElement.contains(document.activeElement);

    try {
      editor.update(
        () => {
          // Parse HTML string to DOM
          const parser = new DOMParser();
          const dom = parser.parseFromString(value, 'text/html');

          // Generate Lexical nodes from DOM
          const nodes = $generateNodesFromDOM(editor, dom);

          // Clear existing content and insert new nodes. The temporary root
          // selection is required so $insertNodes knows where to place content.
          const root = $getRoot();
          root.clear();
          root.select();
          $insertNodes(nodes);

          // Drop the selection when the editor wasn't already focused so the
          // reconciler doesn't pull DOM focus into the contentEditable.
          if (!hadFocus) {
            $setSelection(null);
          }

          // Store the normalized HTML (Lexical's round-trip differs from the
          // raw input) so the export listener that fires for this same update
          // doesn't treat the import as a user edit and emit a spurious
          // onChange.
          previousHtmlRef.current = $generateHtmlFromNodes(editor, null);
        },
        { discrete: true },
      );
    } catch (error) {
      console.error('Error importing HTML into editor:', error);
    }
  }, [editor, value]);

  // Handle HTML export when editor content changes
  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      const onChange = onChangeRef.current;
      if (!onChange) {
        return;
      }

      // Skip if we're currently updating from value prop to avoid loops
      if (isUpdatingRef.current) {
        return;
      }

      try {
        editorState.read(() => {
          const htmlString = $generateHtmlFromNodes(editor, null);

          // Only call onChange if the HTML content actually changed
          if (htmlString !== previousHtmlRef.current) {
            previousHtmlRef.current = htmlString;
            isUpdatingRef.current = true;
            onChange(htmlString);

            // Reset flag after a short delay to allow value prop update to complete
            setTimeout(() => {
              isUpdatingRef.current = false;
            }, 0);
          }
        });
      } catch (error) {
        console.error('Error exporting HTML from editor:', error);
        isUpdatingRef.current = false;
      }
    });
  }, [editor]);

  return null;
}
